import type { ThirdPartyDevice } from "@/api/orval/generated/schemas/thirdPartyDevice";
import { openCardForDeviceId } from "@/services/map-management/open-card-for-map-entity";

import { resolveMarkerWarning } from "../core/resolve-marker-warning";
import { toMapMarker } from "../core/to-map-marker";
import type { MapMarker } from "../types";

/**
 * Projects a Davis station document into the marker that draws it.
 *
 * All this owns is which fields of a station document are its position, name, warnings and click —
 * `toMapMarker` assembles the marker, `components/marker-appearances.ts` holds how it looks, and
 * `sources.ts` owns which documents reach here.
 *
 * Returns null when the document has no usable position — legacy documents can carry null coordinates.
 */
export const toWeatherStationMarker = (
    dto: ThirdPartyDevice,
    { areWarningsHiddenByUser }: { areWarningsHiddenByUser: boolean },
): MapMarker | null =>
    toMapMarker({
        source: "thirdPartyDevice",
        modelId: dto.node_id,
        lat: dto.position?.lat,
        long: dto.position?.lng,
        appearance: "weatherStation",
        label: dto.name,
        // By default, we are scoping these to "site" like gateway markers, no zone selection should dim a station out
        scope: { covers: "site" },
        warning: resolveMarkerWarning({
            warnings: dto.warnings,
            notes: dto.notes,
            areWarningsHiddenByUser,
        }),
        onClick: () => openCardForDeviceId(dto.node_id),
    });
